import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { claraChatSend, claraVoiceTranscribe, claraPipelineReview } from '../lib/api';
import { Sparkle, X, Phone, Check, Copy, Microphone, MicrophoneSlash, ArrowRight, Lightning, Star, ChatTeardropDots, Lightbulb, HandHeart, UsersFour, Megaphone, Eraser, Shield, CalendarCheck, ChartBar, Files, HouseLine, Siren, CaretRight, CaretDown, ClockCounterClockwise, ListChecks, TrendUp, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ACTION_CATEGORIES = [
  {
    id: 'pipeline', label: 'Pipeline Management', icon: ChartBar, defaultOpen: true,
    actions: [
      { icon: ChartBar, label: 'Full pipeline review', prompt: '__PIPELINE_REVIEW__', color: 'text-[#E63935]', special: true, desc: 'Review all files, stale items, and who needs to act' },
      { icon: Files, label: 'Stale files check', prompt: 'Review all hot files that have not been updated in 3 or more days. Tell me which borrowers are stuck, who owns them, and what needs to happen to move them forward. Be specific with names.', color: 'text-blue-500', desc: 'Find files not moving' },
      { icon: CalendarCheck, label: 'Commitment dates at risk', prompt: 'Review all commitment dates that are expiring in the next 14 days. Tell me which ones need extensions, who needs to make calls, and what the plan should be for each one.', color: 'text-amber-500', desc: 'Expiring commitment deadlines' },
      { icon: HouseLine, label: 'Sales contingency status', prompt: 'Review all active sales contingencies where a borrower needs to sell a home before purchasing. What is the status of each? Which ones are at risk? What actions should we take today?', color: 'text-purple-500', desc: 'Home sale tracking' },
      { icon: Siren, label: 'Open escalations', prompt: 'Review all open escalations. Who escalated what, to whom, and what is the current status? What needs to be resolved today?', color: 'text-red-500', desc: 'Unresolved team escalations' },
      { icon: ListChecks, label: 'Unassigned files', prompt: 'Find all files that have no one assigned to them. These are falling through the cracks. Tell me who should own each one and what the next step should be.', color: 'text-orange-500', desc: 'Files with no owner' },
      { icon: TrendUp, label: 'Team productivity check', prompt: 'Based on the data in our tool, which team members have the most stale files? Who is updating their files regularly and who is not? Give me an honest assessment so I can coach my team.', color: 'text-teal-500', desc: 'Who is updating, who is not' },
      { icon: Warning, label: 'Clara flagged files', prompt: 'Review all files that Clara has flagged for follow up. What is the current status of each? Have we followed up? What still needs attention?', color: 'text-rose-500', desc: 'AI-flagged issues still open' },
    ]
  },
  {
    id: 'communication', label: 'Communication', icon: ChatTeardropDots, defaultOpen: false,
    actions: [
      { icon: ChatTeardropDots, label: 'Draft email response', prompt: 'Help me draft a response to this email:\n\n', color: 'text-blue-500', desc: 'Craft the perfect reply' },
      { icon: Phone, label: 'Frustrated client help', prompt: 'A client is frustrated. Help me craft a response that follows our Unreasonable Hospitality principles. Here is the situation:\n\n', color: 'text-red-500', desc: 'De-escalate with empathy' },
      { icon: UsersFour, label: 'Realtor partner email', prompt: 'Write an email to a realtor partner that shows our value and builds the relationship:\n\n', color: 'text-purple-500', desc: 'Build the partnership' },
      { icon: Shield, label: 'Realtor heads up draft', prompt: 'Draft a Realtor heads up message. I need to tell the agent about an issue BEFORE the borrower contacts them. Situation:\n\n', color: 'text-violet-500', desc: 'Get ahead of the narrative' },
      { icon: Lightbulb, label: 'Explain to borrower', prompt: 'Help me explain this to a borrower in simple terms so they feel confident and informed:\n\n', color: 'text-emerald-500', desc: 'Make complex things simple' },
      { icon: Lightning, label: 'Handle objection', prompt: 'Help me handle this objection while maintaining trust and moving forward:\n\n', color: 'text-orange-500', desc: 'Turn resistance into agreement' },
    ]
  },
  {
    id: 'cx', label: 'Customer Experience', icon: Star, defaultOpen: false,
    actions: [
      { icon: Star, label: 'WOW recovery idea', prompt: 'Suggest a WOW recovery action for this situation:\n\n', color: 'text-amber-500', desc: 'Turn problems into loyalty' },
      { icon: HandHeart, label: 'Client check in', prompt: 'Draft a proactive client check in message that makes them feel like they are our only file:\n\n', color: 'text-teal-500', desc: 'Proactive personal touch' },
      { icon: Megaphone, label: 'Realtor value pitch', prompt: 'Help me articulate why working with Eric Hamilton and Rate is the best choice for their clients:\n\n', color: 'text-pink-500', desc: 'Show our value' },
      { icon: ClockCounterClockwise, label: 'Follow up strategy', prompt: 'Help me create a follow up plan for this borrower to make sure nothing falls through the cracks:\n\n', color: 'text-cyan-500', desc: 'Never let them feel forgotten' },
    ]
  }
];

function MessageBubble({ msg }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(msg.text); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[90%] bg-[#E63935] text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed">{msg.text}</div>
      </div>
    );
  }
  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[90%]">
        {msg.isFrustrated && (
          <div className="bg-red-500/15 border border-red-500/40 rounded-xl px-3 py-2 mb-2 flex items-center gap-2">
            <Phone size={16} weight="fill" className="text-red-500 flex-shrink-0" />
            <span className="text-red-500 text-xs font-bold uppercase tracking-wide">Stop, phone call first</span>
          </div>
        )}
        {msg.hasWow && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 mb-2 flex items-center gap-2">
            <Star size={16} weight="fill" className="text-amber-500 flex-shrink-0" />
            <span className="text-amber-500 text-xs font-semibold">WOW Recovery Opportunity</span>
          </div>
        )}
        <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed text-foreground whitespace-pre-wrap">{msg.text}</div>
        <button onClick={handleCopy} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground mt-1 ml-1 transition-colors">
          {copied ? <><Check size={10} weight="bold" /> Copied</> : <><Copy size={10} /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function ActionCategory({ cat, onAction }) {
  const [open, setOpen] = useState(cat.defaultOpen);
  return (
    <div className="mb-2">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
        {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <cat.icon size={14} weight="duotone" />
        <span className="uppercase tracking-wider">{cat.label}</span>
        <span className="text-[10px] text-muted-foreground/50 ml-auto">{cat.actions.length}</span>
      </button>
      {open && (
        <div className="space-y-0.5 mt-0.5 ml-1">
          {cat.actions.map(qa => (
            <button key={qa.label} onClick={() => onAction(qa)}
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-foreground hover:bg-muted border border-transparent hover:border-border transition-all ${qa.special ? 'bg-[#E63935]/10 border-[#E63935]/20' : ''}`}
              data-testid={`clara-qa-${qa.label.split(' ')[0].toLowerCase()}`}>
              <qa.icon size={15} weight={qa.special ? 'fill' : 'duotone'} className={`${qa.color} flex-shrink-0`} />
              <div className="min-w-0">
                <span className={`leading-tight block ${qa.special ? 'font-semibold' : ''}`}>{qa.label}</span>
                <span className="text-[10px] text-muted-foreground leading-tight block">{qa.desc}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClaraAssistant({ currentTab, currentUser }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sessionId] = useState(() => `clara-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (open && inputRef.current && !recording) inputRef.current.focus(); }, [open, recording]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    setMessages(p => [...p, { role: 'user', text: text.trim() }]);
    setInput('');
    setLoading(true);
    try {
      const context = `Page: ${currentTab}, User: ${currentUser?.name || 'Unknown'} (${currentUser?.role || ''})`;
      const res = await claraChatSend(text.trim(), context, sessionId, currentUser?.id);
      setMessages(p => [...p, { role: 'clara', text: res.response, isFrustrated: res.is_frustrated_mode, hasWow: res.has_wow_suggestion }]);
    } catch (e) {
      setMessages(p => [...p, { role: 'clara', text: 'Sorry, I had trouble with that. Please try again.' }]);
    } finally { setLoading(false); }
  }, [loading, currentTab, currentUser, sessionId]);

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } };

  const runPipelineReview = useCallback(async () => {
    setMessages(p => [...p, { role: 'user', text: 'Run a full pipeline review. What needs attention? Who is not updating? What is stale?' }]);
    setLoading(true);
    try {
      const res = await claraPipelineReview();
      const statsLine = `Active: ${res.stats.total_active_files} | Stale: ${res.stats.stale_files} | Urgent: ${res.stats.urgent_files} | Unassigned: ${res.stats.unassigned_files} | Commitments: ${res.stats.commitment_alerts} | Escalations: ${res.stats.open_escalations} | Flagged: ${res.stats.unresolved_flags}`;
      setMessages(p => [...p, { role: 'clara', text: `${statsLine}\n\n${res.review}` }]);
    } catch (e) {
      setMessages(p => [...p, { role: 'clara', text: 'Sorry, I had trouble pulling the pipeline data. Please try again.' }]);
    } finally { setLoading(false); }
  }, []);

  const handleQuickAction = (qa) => {
    if (qa.special && qa.prompt === '__PIPELINE_REVIEW__') {
      runPipelineReview();
    } else {
      setInput(qa.prompt);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('audio', blob, 'recording.webm');
        formData.append('user_id', currentUser?.id || '');
        try {
          const res = await claraVoiceTranscribe(formData);
          if (res.text) { setInput(prev => prev + res.text); toast.success('Voice transcribed'); }
        } catch (e) { toast.error('Voice transcription failed'); }
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setRecording(true);
      toast.success('Listening...');
    } catch (e) { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="clara-open-btn"
          className="fixed bottom-20 right-5 z-[9999] w-14 h-14 rounded-full bg-[#E63935] hover:bg-[#d32f2f] text-white shadow-lg shadow-red-900/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95">
          <Sparkle size={26} weight="fill" />
        </button>
      )}

      {open && (
        <>
          <div className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="fixed top-0 right-0 bottom-0 z-[9999] w-[460px] max-w-[92vw] bg-background border-l-2 border-[#E63935]/30 shadow-2xl shadow-black/40 flex flex-col"
            data-testid="clara-window" style={{ animation: 'slideRight 0.25s ease-out' }}>
            <style>{`@keyframes slideRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

            {/* Header */}
            <div className="bg-[#0D0D0D] px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[#E63935] flex items-center justify-center">
                  <Sparkle size={18} weight="fill" className="text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white leading-tight">Clara</h3>
                  <p className="text-[10px] text-white/50">Your AI assistant for legendary CX</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => { setMessages([]); setInput(''); }}
                  className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors" title="New conversation" data-testid="clara-clear-btn">
                  <Eraser size={16} />
                </button>
                <button onClick={() => setOpen(false)} className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-testid="clara-close-btn">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="clara-messages">
              {messages.length === 0 && (
                <div className="py-2">
                  <div className="text-center mb-3">
                    <Sparkle size={28} weight="duotone" className="text-[#E63935] mx-auto mb-2" />
                    <p className="text-sm font-semibold text-foreground">Hey {currentUser?.name?.split(' ')[0] || 'there'}!</p>
                    <p className="text-xs text-muted-foreground mt-1">Pick an action, use voice, or ask me anything.</p>
                  </div>
                  {ACTION_CATEGORIES.map(cat => (
                    <ActionCategory key={cat.id} cat={cat} onAction={handleQuickAction} />
                  ))}
                </div>
              )}
              {messages.map((msg, i) => <MessageBubble key={`msg-${i}-${msg.role}`} msg={msg} />)}
              {loading && (
                <div className="flex justify-start mb-3">
                  <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#E63935] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#E63935] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#E63935] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-muted-foreground">Clara is thinking...</span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="px-3 pb-3 pt-2 flex-shrink-0 border-t border-border">
              <div className="flex items-end gap-2">
                <button onClick={recording ? stopRecording : startRecording}
                  className={`h-[44px] w-[44px] rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'}`}
                  title={recording ? 'Stop recording' : 'Voice mode'} data-testid="clara-voice-btn">
                  {recording ? <MicrophoneSlash size={18} weight="fill" /> : <Microphone size={18} />}
                </button>
                <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder={recording ? 'Listening... tap mic to stop' : 'Ask Clara anything or paste an email...'}
                  className="flex-1 bg-card border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-[#E63935]/50 min-h-[44px] max-h-[120px]"
                  rows={1} data-testid="clara-input" />
                <Button onClick={() => sendMessage(input)} disabled={!input.trim() || loading}
                  className="h-[44px] w-[44px] rounded-xl bg-[#E63935] hover:bg-[#d32f2f] text-white flex-shrink-0 p-0" data-testid="clara-send-btn">
                  <ArrowRight size={18} weight="bold" />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
